/**
 * Licensed Materials - Property of IBM
 * IBM Watson Analytics (C) Copyright IBM Corp. 2017, 2020
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
	*/
define(['../../lib/@waca/core-client/js/core-client/ui/core/Class',
	'../../widgets/livewidget/query/QueryService',
	'../../widgets/livewidget/query/CommonQueryHelper',
	'underscore'
], function (BaseClass, QueryService, CommonQueryHelper, _) {
	'use strict';

	const ENCODED_NULL = '\u0000';


	/**
	 * The helper class for filters that returns simpler response of CommonQueryHelper.
	 */
	var FilterQueryHelper = BaseClass.extend({

		NUMERIC_DATA_TYPES: [
			'integer',
			'decimal',
			'double',
			'float',
			'long'
		],

		init: function (params) {
			FilterQueryHelper.inherited('init', this, arguments);
			this.dashboard = params.dashboardApi;
			this.logger = this.dashboard.getGlassCoreSvc('.Logger');
			this.itemContext = params.itemContext;
			this.pageContextApi = params.pageContextApi;
			this.metadataInfo = params.metadataInfo;
			this.drillSelection = params.drillSelection;
			this.globalFilters = params.globalFilters;
			this.queryService = params.queryService;
			this.columnInfo = {
				column: {
					columnId: this.itemContext.itemId
				}
			};

			this._initColumnBinningInfo(this.columnInfo);

			this.queryHelper = this._createQueryHelper();
		},

		_initColumnBinningInfo(columnInfo) {
			if (this.itemContext.binning) {
				columnInfo.column.binning = this.itemContext.binning;
				columnInfo.column.binningId = this.itemContext.uniqueId;
				columnInfo.column.binningItemId = this.itemContext.itemId;
			}
		},

		_getGlobalFilterList: function(pageContextScope) {
			pageContextScope = pageContextScope || this.itemContext.scope;
			const { sourceId, itemId } = this.itemContext;
			// The value of pageContextScope can be one of the follow:
			// 1. undefined when creating local (widget-level) filters
			// 2. 'global' for global filter (from filter dock)
			// 3. an id for local (page-level) filters (from filter dock)
			// Therefore, we only want to retrieve filters in case 3
			if (pageContextScope && pageContextScope !== 'global') {
				const filters = this.globalFilters.getFilters(sourceId, 'global');
				return filters.getFilterList().filter(item => item.columnId === itemId);
			}
			return [];
		},

		/**
		 * Execute a query for a specific column data
		 * @param {String} pageContextScope The context of the scope (global or a tab/scene/widget/etc)
		 * @param {String} [searchTerm] A string to provide additional filters on the column
		 * @param {Object} [filterSpec] A filter spec, mainly created from local filter conditions
		 */
		getColumnData: function (pageContextScope, searchTerm, filterSpec) {
			// TODO Should not need pageContextScope but won't re-fact now.
			// When itemId is different, global filter does not provide any context to tab filters.
			// When itemId is the same, global filter is cascaded to tab filters through FilterBaseController.getCurrentPageContextItem
			const { itemId, sourceId } = this.itemContext;
			const dataSources = this.dashboard.getFeature('DataSources');
			const dataSource = dataSources.getDataSource(sourceId);

			return dataSource.loadMetadata().then(() => {
				const filterList = this._getGlobalFilterList(pageContextScope);
				if (filterSpec) {
					filterList.push(filterSpec);
				}

				const mdColumn = dataSource.getMetadataColumn(itemId);
				let sort;
				if (mdColumn) {
					const sortType = mdColumn.getDefaultSortType();
					// Preserve the hierarchical order for Olap column, like we do for visualizations (see VisQueryDataItemBuilder._buildSort)
					if (!sortType && !mdColumn.isOlapColumn() && !mdColumn.isNamedSet()) {
						sort = 'asc';
					}
					if (sortType) {
						sort = sortType;
					}
				}

				let searchTermList;
				if (typeof searchTerm === 'string' && searchTerm.length > 0) {
					searchTermList = [searchTerm];
				}

				return this.queryService.getColumnValues(sourceId, [itemId], filterList, sort, searchTermList).then((queryResult) => {
					const isProperty = (mdColumn && mdColumn.getSourceCategory && mdColumn.getSourceCategory() === 'property');
					const values = isProperty ? this._extractQueryResultDataValuesForProperty(queryResult) : this._extractListOfQueryResultData(queryResult);
					return values;
				});
			}).catch((e) => {
				this.logger.error(e);
				throw(e);
			});
		},

		getColumnDataWithConditions: function(scope, conditions) {
			const filters = {
				columnId: this.itemContext.itemId,
				type: 'pre',
				operator: 'in',
				visualizationId: '',
				conditions: this.convertFromConditionsToQueryFilters(conditions)
			};
			return this.getColumnData(scope, null, filters);
		},

		getMinMax: function() {
			if(this.metadataInfo.isProperty || this.metadataInfo.isStatic) {
				return Promise.resolve([null, null]);
			} else {
				const filters = this._getGlobalFilterList();
				return this.queryService.getColumnMinMaxValues(this.itemContext.sourceId, this.itemContext.itemId, filters)
					.then(queryResult => {
						const min = queryResult.getValue(0, 0).value;
						const max = queryResult.getValue(0, 1).value;
						return [min, max];
					})
					.catch(e => {
						this.logger.error(e);
						return Promise.reject(e);
					});
			}
		},

		getRootMembers: function(pageContextScope) {
			const { itemId, sourceId } = this.itemContext;
			const filterList = this._getGlobalFilterList(pageContextScope);

			return this.queryService.getRootMembers(sourceId, [itemId], filterList).then((queryResult) => {
				if (queryResult.getRowCount() === 1) {
					// Need to query the children when a single root
					const queryDataItem = queryResult.getResultItemList()[0]; // There is only one dataItem in the result
					const rootValue = queryDataItem.getValue(0, 0)[0];
					return this.getChildren(pageContextScope, rootValue.value).then((childrenData) => {
						return [rootValue].concat(childrenData);
					});
				} else {
					return this._extractListOfQueryResultData(queryResult);
				}
			}).catch((error) => {
				this.logger.error(error);
				throw error;
			});
		},

		getChildren: function(pageContextScope, mun, rowLimit, offset) {
			const { itemId, sourceId } = this.itemContext;
			const filterList = this._getGlobalFilterList(pageContextScope);

			return this.queryService.getChildren(sourceId, [itemId], mun, filterList, rowLimit, offset).then((queryResult) => {
				return this._extractListOfQueryResultData(queryResult);
			}).catch((error) => {
				this.logger.error(error);
				throw error;
			});
		},

		// ########## NOTE 1:
		// This hasn't actually been tested.
		// It is thought that this 'siblings' command was originally implemented
		// to offer the full list of hierarchy functions.
		// ########## NOTE 2:
		// If you found your way here through some defect then I suggest you add
		// as much information in the defect as possible to document how the
		// 'siblings' command works/is executed and replace this Note 2 comment
		// with the defect number here.
		getSiblings: function(pageContextScope, mun) {
			const { itemId, sourceId } = this.itemContext;
			const filterList = this._getGlobalFilterList(pageContextScope);

			return this.queryService.getSiblings(sourceId, [itemId], mun, filterList).then((queryResult) => {
				return this._extractListOfQueryResultData(queryResult);
			}).catch((error) => {
				this.logger.error(error);
				throw error;
			});
		},

		getAscendants: function(pageContextScope, muns) {
			const { itemId, sourceId } = this.itemContext;
			const filterList = this._getGlobalFilterList(pageContextScope);

			return this.queryService.getAscendants(sourceId, [itemId], muns, filterList).then((queryResult) => {
				return this._extractListOfQueryResultData(queryResult);
			}).catch((error) => {
				this.logger.error(error);
				throw error;
			});
		},

		/**
		 * Converts a condition object  as returned by the NonNumericFilter common control into the filter format usable by the dataset service.
		 * @param {object} conditions
		 * @return the raw filter expression
		 */
		convertFromConditionsToQueryFilters: function(conditions) {
			return this._convertFromConditionsToQueryFilters(conditions, this.itemContext.itemId);
		},

		/**
		 * Converts a  dataset service.filter into a condition object suitable for the NonNumericFilter common control.
		 * This method is quite limited and can only reliably convert the output 'convertFromConditionsToQueryFilters' and not any DSS filter.
		 * It would be used to de-serialize a persisted filter into a format suitable for the dialog.
		 * @param {object} filters object
		 * @return the conditions object
		 */
		convertFromQueryFiltersToConditions: function(filters) {
			return this._convertFromQueryFiltersToConditions(filters);
		},

		_extractListOfQueryResultData: function(queryResultData) {
			const resultRowSize = queryResultData.getRowCount();
			const aResults = [];

			for (let rowIndex = 0; rowIndex < resultRowSize; rowIndex++) {
				const value = queryResultData.getValue(rowIndex, 0)[0];
				aResults.push(value);
			}

			return aResults;
		},

		_extractQueryResultDataValuesForProperty: function(queryResultData) {
			//For properties, many categories might have the same value.
			//(ie: a color property for a product might be 'red' for many products)
			//We only want to return 1 instance of any value.
			const resultRowSize = queryResultData.getRowCount();
			const uniqueResultMap = {};
			for (let rowIndex = 0; rowIndex < resultRowSize; rowIndex++) {
				let value = queryResultData.getValue(rowIndex, 0)[0];
				let nullKey = _.isNull(value.d) ? ENCODED_NULL : null;
				uniqueResultMap[nullKey || value.d || value] = value;
			}

			let values;
			//Move null value to the top
			let nullVal = uniqueResultMap[ENCODED_NULL];
			if (nullVal){
				delete uniqueResultMap[ENCODED_NULL];
				values = [nullVal].concat(_.values(uniqueResultMap));
			} else {
				values = _.values(uniqueResultMap);
			}
			return values;
		},

		_createQueryHelper: function() {
			var queryService = new QueryService({
				dashboardApi: this.dashboard
			});
			var queryHelper = new CommonQueryHelper({
				'queryService': queryService
			}, this.logger);

			return queryHelper;
		},


		_conditionTypeToOperators: {
			equals: 'in',
			contains: 'containsignorecase',
			startsWith: 'beginswith',
			endsWith: 'endswith'
		},

		_convertFromConditionsToQueryFilters: function(dialogConditions, columnId) {
			const conditionTypeToOperators = this._conditionTypeToOperators;
			let convertPredicate = predicate => {
				let aValues;
				// For cheaper comparison, convert string value to integer value if itemContext is numeric data type.
				if (this.NUMERIC_DATA_TYPES.indexOf(this.itemContext.dataType) !== -1) {
					// Normalize the predicate value to an array of values when input is '19 20', aValues = [19, 20]
					if (predicate.value && predicate.value.split) {
						aValues = predicate.value.split(' ');
					} else {
						aValues = [predicate.value];
					}
					aValues = _.map(aValues, value => {
						// We are assuming the value is a number, it may not be. If it isn't then just add what the user inputed.
						let returnVal = Number(value);
						return isNaN(returnVal) ? value : returnVal;
					});
				} else {
					// Non numeric value will be compared as one string, e.g. 'A1T 2L2' stays as a one string to search as a postal code.
					aValues = [predicate.value];
				}
				let filter = {
					'operator': conditionTypeToOperators[predicate.conditionType],
					'itemId': columnId,
					'ignoreCase': true,
					'values': aValues,
					'valueType': 'display'
				};
				return predicate.invert ? { 'not': filter } : filter;
			};
			let filter = {};
			if( dialogConditions.logicalOperation ){
				filter[dialogConditions.logicalOperation] =  dialogConditions.predicates.map(convertPredicate);
			}else {
				filter = convertPredicate(dialogConditions.predicates[0]);
			}
			return filter;
		},

		_convertFromQueryFiltersToConditions: function(queryFilter) {
			var operatorsToConditionType = _.invert(this._conditionTypeToOperators);

			var convertFilter = function(filter) {
				var inverted = Boolean(filter.not);
				if (inverted) {
					filter = filter.not;
				}
				return {
					conditionType: operatorsToConditionType[filter.operator],
					value: filter.values[0].toString(),
					invert: inverted
				};
			};

			var logicalOperation;
			if (queryFilter.and) {
				logicalOperation = 'and';
			} else if (queryFilter.or) {
				logicalOperation = 'or';
			}
			if (logicalOperation) {
				return {
					logicalOperation: logicalOperation,
					predicates: queryFilter[logicalOperation].map(convertFilter)
				};
			}else{
				return {
					predicates: [convertFilter(queryFilter)]
				};
			}
		},

		remove: function (){
			this.pageContextApi = null;
			this.queryHelper = null;
			this.dashboard = null;
		}
	});
	return FilterQueryHelper;
});
